/**
 * 用户认证服务 v2
 * 支持多角色: admin, process_admin, training_teacher, student
 */

const TOKEN_KEY = 'access_token'
const USER_KEY = 'user_info'
const EXPIRY_KEY = 'token_expiry'

const API_BASE = '/api/v1/auth'

/** 登录（支持验证码） */
export async function login(username, password, captchaCode = '', captchaId = '', captchaType = '') {
  const formData = new URLSearchParams()
  formData.append('username', username)
  formData.append('password', password)
  if (captchaCode) formData.append('captcha_code', captchaCode)
  if (captchaId) formData.append('captcha_id', captchaId)
  if (captchaType) formData.append('captcha_type', captchaType)

  const response = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || `登录失败 (${response.status})`)
  }

  const data = await response.json()
  setToken(data.access_token)
  setUser(data.user)
  return data
}

/** 获取验证码配置（公开接口） */
export async function getCaptchaConfig() {
  const response = await fetch(`${API_BASE}/captcha/config`)
  if (!response.ok) return { captchaType: 'none' }
  return response.json()
}

/** 获取验证码（返回 captchaId + imageBase64） */
export async function getCaptcha() {
  const response = await fetch(`${API_BASE}/captcha`)
  if (!response.ok) return { captchaId: '', imageBase64: '' }
  return response.json()
}

/** 获取验证码状态（兼容旧接口） */
export async function getCaptchaStatus() {
  const response = await fetch(`${API_BASE}/captcha/status`)
  if (!response.ok) return { mode: 'none' }
  return response.json()
}

/** 注册 */
export async function register(data) {
  const response = await fetch(`${API_BASE}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || '注册失败')
  }
  return response.json()
}

/** 登出 */
export async function logout() {
  try {
    const token = getToken()
    if (token) {
      await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
    }
  } catch (e) { /* ignore */ }
  clearAuth()
}

/** 获取当前用户信息 */
export async function getCurrentUser() {
  try {
    const token = getToken()
    if (!token) return null
    const response = await fetch(`${API_BASE}/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!response.ok) { clearAuth(); return null }
    const data = await response.json()
    return data.user
  } catch { return null }
}

/** 修改密码 */
export async function changePassword(oldPassword, newPassword) {
  const token = getToken()
  const response = await fetch(`${API_BASE}/change-password`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || '修改密码失败')
  }
  return response.json()
}

export function isAuthenticated() {
  const token = getToken()
  if (!token) return false
  const expiry = localStorage.getItem(EXPIRY_KEY)
  if (expiry && new Date(expiry).getTime() < Date.now()) {
    clearAuth()
    return false
  }
  return true
}

export function hasRole(...roles) {
  const user = getUser()
  return user && roles.includes(user.role)
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * 带鉴权的 fetch 封装：自动附加 Authorization Bearer token。
 * 后端自 B213 起全部 API 路由强制登录（路由级 Depends），
 * 前端禁止再用裸 fetch 调 /api/* —— 一律走本函数或 services/api.js 的 axios 实例。
 */
export function authFetch(url, options = {}) {
  const token = getToken()
  const headers = { ...(options.headers || {}) }
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return fetch(url, { ...options, headers })
}

function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token)
  const expiry = new Date(Date.now() + 2 * 60 * 60 * 1000)
  localStorage.setItem(EXPIRY_KEY, expiry.toISOString())
}

export function getUser() {
  const str = localStorage.getItem(USER_KEY)
  return str ? JSON.parse(str) : null
}

function setUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  localStorage.removeItem(EXPIRY_KEY)
}

export function getAuthHeader() {
  const token = getToken()
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

/** Admin API helper */
async function adminFetch(url, options = {}) {
  const token = getToken()
  const res = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `请求失败 (${res.status})`)
  }
  return res.json()
}

// ── Admin API wrappers ──
const ADMIN = '/api/v1/admin'

export const adminApi = {
  // Users
  listUsers: (params = {}) => {
    const q = new URLSearchParams(params).toString()
    return adminFetch(`${ADMIN}/users?${q}`)
  },
  getUser: (id) => adminFetch(`${ADMIN}/users/${id}`),
  createUser: (data) => adminFetch(`${ADMIN}/users`, { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => adminFetch(`${ADMIN}/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => adminFetch(`${ADMIN}/users/${id}`, { method: 'DELETE' }),
  pendingApprovals: (params = {}) => {
    const q = new URLSearchParams(params).toString()
    return adminFetch(`${ADMIN}/users/pending/list?${q}`)
  },
  approveUser: (data) => adminFetch(`${ADMIN}/users/approve`, { method: 'POST', body: JSON.stringify(data) }),
  resetPassword: (id, data) => adminFetch(`${ADMIN}/users/reset-password/${id}`, { method: 'POST', body: JSON.stringify(data) }),
  // Roles
  listRoles: () => adminFetch(`${ADMIN}/roles`),
  createRole: (data) => adminFetch(`${ADMIN}/roles`, { method: 'POST', body: JSON.stringify(data) }),
  updateRole: (id, data) => adminFetch(`${ADMIN}/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRole: (id) => adminFetch(`${ADMIN}/roles/${id}`, { method: 'DELETE' }),
  // Permissions
  listPermissions: (roleId) => adminFetch(`${ADMIN}/permissions${roleId ? `?role_id=${roleId}` : ''}`),
  updatePermissions: (data) => adminFetch(`${ADMIN}/permissions/batch`, { method: 'PUT', body: JSON.stringify(data) }),
  // Logs
  listLogs: (params = {}) => {
    const q = new URLSearchParams(params).toString()
    return adminFetch(`${ADMIN}/logs?${q}`)
  },
  // Config
  getConfig: (key) => adminFetch(`${ADMIN}/config${key ? `?key=${key}` : ''}`),
  updateConfig: (data) => adminFetch(`${ADMIN}/config`, { method: 'PUT', body: JSON.stringify(data) }),
}
